import {
  API_ROUTES,
  customerRowSchema,
  invoiceDocumentSchema,
  invoiceRowSchema,
  invoiceSettingsSchema,
  type CreateCustomerRequest,
  type SaveInvoiceRequest,
  type UpdateInvoiceSettingsRequest,
} from '@hvac/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiClient } from './queries.js';

export const invoiceKeys = {
  all: (organizationId: string) => ['invoices', organizationId] as const,
  list: (organizationId: string) => ['invoices', organizationId, 'list'] as const,
  one: (organizationId: string, invoiceId: string) =>
    ['invoices', organizationId, 'one', invoiceId] as const,
  settings: (organizationId: string) => ['invoices', organizationId, 'settings'] as const,
};

export const invoicesQuery = (organizationId: string) =>
  queryOptions({
    queryKey: invoiceKeys.list(organizationId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.invoices.list(organizationId),
        schema: z.array(invoiceRowSchema),
        signal,
      }),
    staleTime: 15_000,
  });

export const invoiceQuery = (organizationId: string, invoiceId: string) =>
  queryOptions({
    queryKey: invoiceKeys.one(organizationId, invoiceId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.invoices.byId(organizationId, invoiceId),
        schema: invoiceDocumentSchema,
        signal,
      }),
    staleTime: 15_000,
  });

export const invoiceSettingsQuery = (organizationId: string) =>
  queryOptions({
    queryKey: invoiceKeys.settings(organizationId),
    queryFn: ({ signal }) =>
      apiClient.request({
        path: API_ROUTES.invoices.settings(organizationId),
        schema: invoiceSettingsSchema,
        signal,
      }),
    staleTime: 60_000,
  });

export const saveInvoice = (
  organizationId: string,
  invoiceId: string | null,
  body: SaveInvoiceRequest,
) =>
  apiClient.request({
    method: invoiceId === null ? 'POST' : 'PATCH',
    path:
      invoiceId === null
        ? API_ROUTES.invoices.list(organizationId)
        : API_ROUTES.invoices.byId(organizationId, invoiceId),
    schema: z.object({ invoiceId: z.string() }),
    body,
  });

export const issueInvoice = (organizationId: string, invoiceId: string) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.invoices.issue(organizationId, invoiceId),
    schema: z.object({ transactionId: z.string() }),
  });

export const voidInvoice = (organizationId: string, invoiceId: string, reason: string) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.invoices.void(organizationId, invoiceId),
    schema: z.undefined(),
    body: { reason },
  });

export const updateInvoiceSettings = (organizationId: string, body: UpdateInvoiceSettingsRequest) =>
  apiClient.request({
    method: 'PATCH',
    path: API_ROUTES.invoices.settings(organizationId),
    schema: invoiceSettingsSchema,
    body,
  });

export const createCustomer = (organizationId: string, body: CreateCustomerRequest) =>
  apiClient.request({
    method: 'POST',
    path: API_ROUTES.customers.list(organizationId),
    schema: customerRowSchema,
    body,
  });
